import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef } from 'react';
/**
 * Accessible modal built on the native <dialog> element.
 *
 * The platform provides, correctly and for free: focus trapping, Escape to
 * dismiss, focus restore to the trigger on close, and inerting the rest of the
 * document. We add only backdrop-click dismissal and body scroll locking.
 *
 * Two things here do not trust the top layer, because the Android WebView the
 * games ship in does not give it to us:
 *
 *  - The dim is a plain element, not `::backdrop`. Tailwind compiles
 *    `backdrop:bg-black/60` to `color-mix(… var(--color-black) …)`, and
 *    `::backdrop` does not inherit custom properties from its originating
 *    element on Chromium before 122, so the var is undefined, the declaration
 *    is invalid at computed-value time and the dim falls back to transparent.
 *
 *  - `fixed z-[100]` stacks the dialog explicitly. Without the top layer the
 *    dialog is just a positioned box at `z-index: auto`, and anything on the
 *    page whose computed `opacity` is below 1 — a `disabled:opacity-40` button,
 *    an `animate-pulse` dot — paints atomically in the same stage as positioned
 *    boxes, in tree order. Sitting later in the DOM, it landed *on top of* the
 *    sheet: the lobby's greyed-out START GAME button and its pulsing "waiting"
 *    dots showed straight through the invite sheet. An explicit z-index puts
 *    the dialog above every such element regardless of tree order, and is inert
 *    where the top layer does work.
 */
export function Modal({ open, onClose, children, title, placement = 'center', panelClassName = '', panelStyle, dismissable = true, }) {
    const ref = useRef(null);
    // Guards the `close` event from re-entering onClose when the parent has
    // already flipped `open` to false and we called close() ourselves.
    const closingRef = useRef(false);
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        if (open && !el.open) {
            el.showModal();
        }
        else if (!open && el.open) {
            closingRef.current = true;
            el.close();
            closingRef.current = false;
        }
    }, [open]);
    // showModal() blocks interaction with the page but not scrolling behind it.
    useEffect(() => {
        if (!open)
            return;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previous;
        };
    }, [open]);
    const handleClose = useCallback(() => {
        if (closingRef.current)
            return;
        onClose();
    }, [onClose]);
    const handleCancel = useCallback((e) => {
        if (!dismissable)
            e.preventDefault();
    }, [dismissable]);
    // Everything outside the panel — the dialog box, the dim, the gutter around
    // the panel — dismisses. The panel itself is not marked, so clicks inside it
    // (and inside its children) fall through to the panel and are kept.
    const handleBackdropClick = useCallback((e) => {
        if (!dismissable)
            return;
        const target = e.target;
        if (target === ref.current || target.hasAttribute?.('data-modal-dismiss'))
            onClose();
    }, [dismissable, onClose]);
    const sheet = placement === 'sheet';
    return (_jsxs("dialog", { ref: ref, "aria-label": title, onClose: handleClose, onCancel: handleCancel, onClick: handleBackdropClick, className: [
            // Neutralise the UA dialog box so it becomes a full-viewport container.
            'fixed inset-0 z-[100] m-0 max-h-none max-w-none border-0 bg-transparent p-0',
            'h-full w-full overflow-y-auto overscroll-contain',
        ].join(' '), children: [_jsx("div", { "aria-hidden": "true", "data-modal-dismiss": true, className: "fixed inset-0 bg-black/60" }), _jsx("div", { "data-modal-dismiss": true, className: [
                    'relative flex min-h-full w-full flex-col items-center px-4',
                    sheet ? 'justify-end pt-4' : 'justify-center py-4',
                ].join(' '), style: sheet ? { paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' } : undefined, children: _jsx("div", { className: `shrink-0 ${panelClassName}`, style: panelStyle, children: children }) })] }));
}
//# sourceMappingURL=Modal.js.map